// copy — what the view tree may ask the shell to put on the clipboard.
//
// The clipboard is device I/O, so a component never touches it: it emits a
// typed `CopyRequest` through this context and the composition root
// (AppShellHost) formats the text (`copyTextOf`, pure), writes it via
// `ports.copyText` and confirms with a toast. No provider (a test, a gate
// outside the shell) means no copy affordance, never a fake one.

import { exhaustSwitchCase } from "@bilda/utils";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { createContext, useContext } from "react";

export type CopyRequest =
    // The version label: "version (build)".
    | { readonly type: "version" }
    // "Copy error details": the error ID, "version (build)" and the time;
    // none of them is ever shown on screen.
    | { readonly type: "error-info"; readonly errorId: string };

export type Copy = (request: CopyRequest) => void;

// What goes on the clipboard, and how the confirmation names it. Error
// details are named, never echoed: the toast must not show the error ID or
// version (SF-14).
export type CopyText = { readonly text: string; readonly label: string };

// `version`: "version (build)"; `time`: when the copy happened (the shell
// reads the clock).
export const copyTextOf = (
    i18n: I18n,
    request: CopyRequest,
    version: string,
    time: string,
): CopyText => {
    switch (request.type) {
        case "version": {
            const text = i18n._(msg`version ${version}`);
            return { text, label: text };
        }
        case "error-info": {
            // Label-value pairs, each label its own message without
            // placeholders: the values stay intact even before a translation
            // exists (a production build doesn't interpolate an uncatalogued
            // message).
            const pairs: readonly (readonly [string, string])[] = [
                [i18n._(msg`Error ID`), request.errorId],
                [i18n._(msg`Version`), version],
                [i18n._(msg`Time`), time],
            ];
            return {
                text: pairs
                    .map(([name, value]) => `${name}: ${value}`)
                    .join(" · "),
                label: i18n._(msg`error details`),
            };
        }
        default:
            return exhaustSwitchCase(request);
    }
};

const CopyContext = createContext<Copy | null>(null);

export const CopyProvider = CopyContext.Provider;

export const useCopy = (): Copy | null => useContext(CopyContext);
